import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  Shift,
  ShiftNeed,
  ShiftsService,
  TimesheetService,
  TimesheetWithEntry,
  BILLING_STATUS_BADGE_PROPS,
  ShiftNeedService
} from '@umss/shared/data-access';
import { ToastService } from '../../services/toast.service';
import { UmssBadgeComponent } from '../badge/badge.component';
import { UmssButtonComponent } from '../button/button.component';
import { UmssDialogComponent } from '../dialog/dialog.component';
import { UmssDialogHeaderComponent } from '../dialog-header/dialog-header.component';
import { UmssTextareaComponent } from '../textarea/textarea.component';
import { UmssTooltipDirective } from '../../directives/tooltip.directive';
import { UmssHasPermissionDirective } from '../../directives/has-permission.directive';
import { TimeFormatPipe, TimeRangePipe } from '@umss/shared/utils';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';

@Component({
  selector: 'umss-timesheet-modal',
  imports: [
    UmssDialogComponent,
    UmssDialogHeaderComponent,
    UmssButtonComponent,
    UmssTextareaComponent,
    ReactiveFormsModule,
    UmssBadgeComponent,
    UmssTooltipDirective,
    TimeRangePipe,
    DatePipe,
    CurrencyPipe,
    TimeFormatPipe,
    UmssSkeletonComponent,
    UmssHasPermissionDirective
  ],
  templateUrl: './timesheet-modal.component.html',
  styleUrls: ['./timesheet-modal.component.scss']
})
export class TimesheetModalComponent implements OnChanges {
  /** Open input pass to the modal */
  readonly open = input<boolean>(false);
  /** Timesheet value */
  readonly timesheet = input.required<TimesheetWithEntry>();
  /** Flag to indicate if initial timesheet entry is loading (used by openById) */
  readonly isLoadingInitialEntry = input<boolean>(false);

  private toastService = inject(ToastService);

  readonly closed = output<'rejected' | 'approved' | undefined>();
  /** Form control to control textarea component */
  protected rejectionReasonFC = new FormControl<string | null>(null);
  protected readonly badgeProps = BILLING_STATUS_BADGE_PROPS;

  readonly timesheetService = inject(TimesheetService);
  readonly shiftService = inject(ShiftsService);
  readonly shiftNeedService = inject(ShiftNeedService);

  /** Flag if user tries to reject without a rejection message */
  rejectingWithoutReason = signal<boolean | undefined>(undefined);

  // ─── Loading States ──────────────────────────────────────────────────────

  /** Flag if shift data is loading */
  private loadingShift = signal(false);

  /** Flag if shift need data is loading */
  private loadingShiftNeed = signal(false);

  /** Computed flag if all content is loaded */
  isLoadingContent = computed(
    () =>
      this.isLoadingInitialEntry() ||
      this.loadingShift() ||
      this.loadingShiftNeed()
  );

  /** Flag if an action (approve/reject) is being processed */
  isProcessingAction = signal(false);
  /** Shift value */
  shift = signal<Shift | null>(null);

  /** ShiftNeed value */
  shiftNeed = signal<ShiftNeed | null>(null);

  // ─── Computed properties for billing breakdown ──────────────────────────────

  /** Contractor hourly rate based on shift urgency */
  readonly contractorHourlyRate = computed(() => {
    const sn = this.shiftNeed();
    if (!sn?.rateInfo) return 0;
    const isUrgent = sn.isUrgent;
    return (
      (isUrgent
        ? (sn.rateInfo.contractorUrgentFinalHourlyRate ??
          sn.rateInfo.contractorUrgentHourlyRate)
        : (sn.rateInfo.contractorNormalFinalHourlyRate ??
          sn.rateInfo.contractorNormalHourlyRate)) ?? 0
    );
  });

  /** Differential hourly rate */
  readonly differentialHourlyRate = computed(() => {
    const sn = this.shiftNeed();
    if (!sn) return 0;
    return sn.differentialRate ?? sn.rateInfo?.differentialRate ?? 0;
  });

  /** Bonus hourly rate (only for urgent shifts) */
  readonly bonusHourlyRate = computed(() => {
    const sn = this.shiftNeed();
    if (!sn?.isUrgent) return 0;
    return sn.bonus ?? 0;
  });

  /** Show bonus row in billing breakdown */
  readonly showBonusRow = computed(() => {
    const sn = this.shiftNeed();
    return sn?.isUrgent && (sn?.bonus ?? 0) > 0;
  });

  /** Total differential amount (differential * hoursWorked) */
  readonly totalDifferentialAmount = computed(() => {
    return this.differentialHourlyRate() * this.timesheet().hoursWorked;
  });

  /** Total bonus amount (bonus * hoursWorked) */
  readonly totalBonusAmount = computed(() => {
    return this.bonusHourlyRate() * this.timesheet().hoursWorked;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue && !this.isLoadingInitialEntry()) {
      this.loadShift();
    } else if (changes['isLoadingInitialEntry']?.currentValue === false) {
      // Trigger load when initial entry finishes loading
      this.loadShift();
    }
  }

  private loadShift(): void {
    if (!this.timesheet().shiftId) return;
    if (this.shift()?.id === this.timesheet().shiftId) return;

    this.loadingShift.set(true);
    this.shiftService.getShiftById(this.timesheet().shiftId).subscribe({
      next: (shift: Shift) => {
        this.shift.set(shift);
        this.loadingShift.set(false);
        this.loadShiftNeed();
      },
      error: (error: HttpErrorResponse) => {
        this.loadingShift.set(false);
        this.toastService.openToast({
          type: 'Error',
          title: error.error.message || 'An error has occured'
        });
      }
    });
  }

  private loadShiftNeed(): void {
    if (!this.shift()?.shiftNeedId) return;
    if (this.shiftNeed()?.id === this.shift()!.shiftNeedId) return;

    this.loadingShiftNeed.set(true);
    this.shiftNeedService.getOneShiftNeed(this.shift()!.shiftNeedId).subscribe({
      next: (shiftNeed: ShiftNeed) => {
        this.shiftNeed.set(shiftNeed);
        this.loadingShiftNeed.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.loadingShiftNeed.set(false);
        this.toastService.openToast({
          type: 'Error',
          title: error.error.message || 'An error has occured'
        });
      }
    });
  }

  /** Close dialog */
  closeDialog(response?: 'rejected' | 'approved'): void {
    this.closed.emit(response);
  }

  /** Handle rejection of a timesheet */
  reject(): void {
    const value = this.rejectionReasonFC.value;

    this.rejectingWithoutReason.set(!value);
    if (!value) return;
    this.isProcessingAction.set(true);
    this.timesheetService
      .reject(this.timesheet().timesheetId, value)
      .subscribe({
        next: () => {
          this.isProcessingAction.set(false);
          this.closeDialog('rejected');
        },
        error: (error: HttpErrorResponse) => {
          this.isProcessingAction.set(false);
          this.toastService.openToast({
            type: 'Error',
            title: error.error.message || 'An error has occured'
          });
        }
      });
  }

  /** Handle approving of a timesheet */
  approve(): void {
    this.isProcessingAction.set(true);
    this.timesheetService.approve(this.timesheet().timesheetId).subscribe({
      next: () => {
        this.isProcessingAction.set(false);
        this.closeDialog('approved');
      },
      error: (error: HttpErrorResponse) => {
        this.isProcessingAction.set(false);
        this.toastService.openToast({
          type: 'Error',
          title: error.error.message || 'An error has occured'
        });
      }
    });
  }
}
